// EmailTemplateSelection.jsx – original card/grid UI with single *global* search bar
import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  Stack,
  TextField,
  Typography,
  Tooltip,
  ImageListItemBar,
  IconButton,
} from "@mui/material";
import { useTheme } from "@emotion/react";
import RefreshIcon from "../../../styles/icons/RefreshIcon";

export default function EmailTemplateSelection({
  open,
  setSelected,
  setClosed,
  templates,
  refreshTemplates,
}) {
  const theme = useTheme();
  const primaryColor = theme.palette.text.primary;

  /** ------------------------------------------------------------------
   * Global search state & logic
   * ------------------------------------------------------------------ */
  const [query, setQuery] = useState("");
  const [filteredItems, setFilteredItems] = useState([]);
  const [temp, setTemp] = useState(null);

  const fallbackImageSrc = "/path/to/fallback.png";

  useEffect(() => {
    if (!query.trim()) {
      setFilteredItems(templates);
      return;
    }

    const tokens = query.toLowerCase().trim().split(/\s+/).filter(Boolean);

    const matchesAllTokens = (item) => {
      const haystacks = [
        item.Id?.toString().toLowerCase() ?? "",
        item.Name?.toLowerCase() ?? "",
        (item.Content ?? item.HtmlBody ?? "").toLowerCase(),
        (item.Description ?? "").toLowerCase(),
      ];
      return tokens.every((token) => haystacks.some((h) => h.includes(token)));
    };

    setFilteredItems(templates.filter(matchesAllTokens));
  }, [query, templates]);

  const handleSubmit = () => {
    if (temp) {
      setSelected(temp);
      setClosed();
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          height: "90vh",
          width: 800,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Sticky Search Bar */}
      <Box
        sx={{
          backgroundColor: "background.default",
          position: "sticky",
          top: 0,
          zIndex: 10,
          borderBottom: "1px solid #ccc",
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        <TextField
          label="Search"
          placeholder="Id, name, content …"
          size="small"
          variant="outlined"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <IconButton aria-label="Refresh" onClick={refreshTemplates}>
          <RefreshIcon sx={{ color: "text.primary" }} />
        </IconButton>
      </Box>

      {/* Scrollable Card Grid */}
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: "background.paper",
          overflowY: "auto",
          p: 2,
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: 3,
        }}
      >
        {filteredItems.length ? (
          filteredItems.map((item) => (
            <Box
              key={item.Id}
              onClick={() => setTemp(item)}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "center",
                border: 1,
                borderRadius: 2,
                boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
                minHeight: 200,
                maxHeight: 200,
                backgroundColor: "background.paper",
                overflow: "hidden",
                minWidth: 200,
                maxWidth: 200,
                position: "relative",
                transition: "box-shadow 0.3s ease-in-out",
                "&:hover": {
                  boxShadow: "0px 4px 15px rgba(0, 0, 0, 0.3)",
                },
              }}
            >
              {/* Title Bar */}
              <Tooltip title={item.Name} placement="top">
                <ImageListItemBar
                  title={item.Name}
                  sx={{
                    backgroundColor: "background.default",
                    width: "100%",
                    textOverflow: "ellipsis",
                    textAlign: "center",
                    position: "static",
                    fontWeight: 500,
                    "& .MuiImageListItemBar-title": { color: primaryColor },
                  }}
                />
              </Tooltip>

              {/* Image Preview with fallback */}
              <Box
                component="img"
                src={item.PreviewUrl}
                alt={item.Name}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = fallbackImageSrc;
                }}
                sx={{
                  flexGrow: 1,
                  width: "100%",
                  objectFit: "cover",
                }}
              />

              {/* Select Button Bar */}
              <ImageListItemBar
                sx={{
                  backgroundColor: "#0da2e1",
                  height: "20%",
                  width: "100%",
                  position: "static",
                  borderBottomLeftRadius: 8,
                  borderBottomRightRadius: 8,
                  cursor: "pointer",
                  textAlign: "center",
                }}
                title="Select"
              />
            </Box>
          ))
        ) : (
          <Typography>No Templates Found</Typography>
        )}
      </Box>

      {/* Footer Buttons */}
      <Box sx={{ p: 2, display: "flex", flexDirection: "row-reverse", gap: 1 }}>
        <Button
          variant="contained"
          disabled={!temp}
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            backgroundColor: theme.palette.companyBlue.main,
            color: "text.primary",
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
        <Button
          variant="outlined"
          sx={{
            minWidth: "7vw",
            borderRadius: 10,
            textTransform: "none",
            color: "text.primary",
          }}
          onClick={setClosed}
        >
          Cancel
        </Button>
      </Box>
    </Dialog>
  );
}
